import React, { useEffect } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import {
  fetchBillerOrderDetailsAsync,
  setSelectedOrderNeedsRefresh,
} from '../billerOrdersSlice';
import OrderDetails from './OrderDetails';
import { CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface OrderDetailsContainerProps {
  restaurantId: string;
  orderId: string;
}

const OrderDetailsContainer: React.FC<OrderDetailsContainerProps> = ({
  restaurantId,
  orderId,
}) => {
  const dispatch = useAppDispatch();
  const { selectedOrder, isLoading, error } = useAppSelector(
    (state) => state.billerOrders
  );
  const { t } = useTranslation();

  // Always fetch details when orderId or restaurantId changes
  useEffect(() => {
    if (orderId && restaurantId) {
      dispatch(fetchBillerOrderDetailsAsync({ restaurantId, orderId })).then(
        () => dispatch(setSelectedOrderNeedsRefresh(false))
      );
    }
  }, [orderId, restaurantId, dispatch]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
        }}
      >
        <CircularProgress size={40} thickness={4} />
      </div>
    );
  }
  // Function to handle retry from OrderDetails component
  const handleRetry = () => {
    if (orderId && restaurantId) {
      dispatch(fetchBillerOrderDetailsAsync({ restaurantId, orderId })).then(
        () => dispatch(setSelectedOrderNeedsRefresh(false))
      );
    }
  };

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading order details: {error}
        </Alert>
        <button
          onClick={handleRetry}
          style={{
            padding: '8px 16px',
            fontWeight: 500,
            borderRadius: 4,
            border: 'none',
            background: '#1976d2',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }
  if (!selectedOrder || selectedOrder.id?.toString() !== orderId.toString()) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
        }}
      >
        <Alert severity="info">No order selected.</Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
        }}
      >
        <CircularProgress size={40} thickness={4} />
      </div>
    );
  }

  return <OrderDetails order={selectedOrder} restaurantId={restaurantId} />;
};

export default OrderDetailsContainer;
